/**
 * Persistência da Calculadora Planilha — lista grande só no IndexedDB.
 * localStorage fica só com resumo (evita estourar quota e quebrar login das janelas).
 */

import type { GrupoCalcPlanilha, LinhaCalcPlanilha } from './calculadoraPlanilhaExcel';

export const LS_CALC_PLANILHA = 'ade-calc-planilha-v1';
export const CALC_PLANILHA_DB = 'ade-calc-planilha-db';
export const CALC_PLANILHA_DB_VERSION = 1;
const STORE = 'estado';
const KEY = 'planilha';

export interface EstadoCalcPlanilha {
  linhas: LinhaCalcPlanilha[];
  meta: number;
  nomeArquivo: string;
  salvoEm: string;
  grupos: GrupoCalcPlanilha[];
}

export function estadoCalcPlanilhaVazio(): EstadoCalcPlanilha {
  return { linhas: [], meta: 725, nomeArquivo: '', salvoEm: '', grupos: [] };
}

function gruposDeEstado(p: Partial<EstadoCalcPlanilha> | null | undefined): GrupoCalcPlanilha[] {
  if (!p || !Array.isArray(p.grupos)) return [];
  return p.grupos
    .filter((g) => g && Array.isArray(g.idsLinhas) && g.idsLinhas.length > 0)
    .map((g) => ({
      id: String(g.id || ''),
      idsLinhas: g.idsLinhas.map(String),
      rotulo: String(g.rotulo || ''),
      metaDoGrupo: Number(g.metaDoGrupo) > 0 ? Number(g.metaDoGrupo) : 0,
    }));
}

function abrirDb(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const req = indexedDB.open(CALC_PLANILHA_DB, CALC_PLANILHA_DB_VERSION);
    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains(STORE)) db.createObjectStore(STORE);
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
}

function lerLs(): EstadoCalcPlanilha | null {
  try {
    const raw = localStorage.getItem(LS_CALC_PLANILHA);
    if (!raw) return null;
    const p = JSON.parse(raw) as EstadoCalcPlanilha;
    if (!p || typeof p !== 'object') return null;
    if (!Array.isArray(p.linhas)) {
      return {
        linhas: [],
        meta: Number(p.meta) || 725,
        nomeArquivo: String(p.nomeArquivo || ''),
        salvoEm: String(p.salvoEm || ''),
        grupos: gruposDeEstado(p),
      };
    }
    return { ...p, grupos: gruposDeEstado(p) };
  } catch {
    return null;
  }
}

function gravarLsResumo(estado: EstadoCalcPlanilha): void {
  const resumo = {
    meta: estado.meta,
    nomeArquivo: estado.nomeArquivo,
    salvoEm: estado.salvoEm,
    linhasCount: estado.linhas.length,
    gruposCount: Array.isArray(estado.grupos) ? estado.grupos.length : 0,
  };
  try {
    localStorage.setItem(LS_CALC_PLANILHA, JSON.stringify(resumo));
  } catch {
    try {
      localStorage.removeItem(LS_CALC_PLANILHA);
    } catch {
      /* quota */
    }
  }
}

function estadoDeDumpLs(raw: string): EstadoCalcPlanilha {
  const p = JSON.parse(raw) as EstadoCalcPlanilha;
  return {
    linhas: Array.isArray(p.linhas) ? p.linhas : [],
    meta: Number(p.meta) || 725,
    nomeArquivo: String(p.nomeArquivo || ''),
    salvoEm: String(p.salvoEm || ''),
    grupos: gruposDeEstado(p),
  };
}

/** Grava dump antigo do LS no IndexedDB e deixa só o resumo no LS. */
export async function resgatarDumpLsCalcPlanilha(raw: string | null): Promise<void> {
  if (!raw) return;
  try {
    const estado = estadoDeDumpLs(raw);
    if (estado.linhas.length > 0) await gravarIdb(estado);
    gravarLsResumo(estado);
  } catch {
    gravarLsResumo(estadoCalcPlanilhaVazio());
  }
}

async function lerIdb(): Promise<EstadoCalcPlanilha | null> {
  try {
    const db = await abrirDb();
    const data = await new Promise<EstadoCalcPlanilha | null>((resolve, reject) => {
      const tx = db.transaction(STORE, 'readonly');
      const r = tx.objectStore(STORE).get(KEY);
      r.onsuccess = () => resolve((r.result as EstadoCalcPlanilha) ?? null);
      r.onerror = () => reject(r.error);
    });
    db.close();
    return data && Array.isArray(data.linhas)
      ? { ...data, grupos: gruposDeEstado(data) }
      : null;
  } catch {
    return null;
  }
}

async function gravarIdb(estado: EstadoCalcPlanilha): Promise<void> {
  try {
    const db = await abrirDb();
    await new Promise<void>((resolve, reject) => {
      const tx = db.transaction(STORE, 'readwrite');
      tx.oncomplete = () => resolve();
      tx.onerror = () => reject(tx.error);
      tx.objectStore(STORE).put(estado, KEY);
    });
    db.close();
  } catch {
    /* ignora */
  }
}

function preferir(a: EstadoCalcPlanilha | null, b: EstadoCalcPlanilha | null): EstadoCalcPlanilha {
  const va = a && Array.isArray(a.linhas) ? a : null;
  const vb = b && Array.isArray(b.linhas) ? b : null;
  if (!va || va.linhas.length === 0) return vb ?? va ?? estadoCalcPlanilhaVazio();
  if (!vb || vb.linhas.length === 0) return va;
  if (vb.linhas.length > va.linhas.length) return vb;
  if (va.linhas.length > vb.linhas.length) return va;
  const ta = Date.parse(va.salvoEm || '') || 0;
  const tb = Date.parse(vb.salvoEm || '') || 0;
  return tb > ta ? vb : va;
}

function congelarMetaGrupos(estado: EstadoCalcPlanilha): EstadoCalcPlanilha {
  const caixa = Number(estado.meta) > 0 ? Number(estado.meta) : 725;
  return {
    ...estado,
    grupos: estado.grupos.map((g) => ({
      ...g,
      metaDoGrupo: Number(g.metaDoGrupo) > 0 ? Number(g.metaDoGrupo) : caixa,
    })),
  };
}

export async function hidratarCalcPlanilha(): Promise<EstadoCalcPlanilha> {
  const ls = lerLs();
  const idb = await lerIdb();
  const unido = preferir(ls, idb);
  const estado = congelarMetaGrupos({ ...unido, grupos: gruposDeEstado(unido) });
  await gravarIdb(estado);
  gravarLsResumo(estado);
  return estado;
}

export async function salvarCalcPlanilha(estado: EstadoCalcPlanilha): Promise<void> {
  const next = { ...estado, salvoEm: new Date().toISOString() };
  await gravarIdb(next);
  gravarLsResumo(next);
}
